import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { 
  Mic, 
  MicOff, 
  Square, 
  Loader2, 
  Save,
  Trash2,
  Edit,
  Clock,
  FileAudio
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import { cn } from '../../lib/utils';

// =============================================
// AUDIO DICTATION COMPONENT
// Record audio and transcribe to text using Whisper
// =============================================

const AudioDictation = ({ 
  onTranscriptionComplete,
  onSave,
  initialText = '',
  appointmentId = null,
  clientId = null,
  className = '',
  placeholder = "Dictez votre compte-rendu ou tapez-le directement...",
  showSaveButton = true
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [text, setText] = useState(initialText);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);

  // Update text when initialText changes
  useEffect(() => {
    setText(initialText);
  }, [initialText]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Format recording time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Start audio level monitoring
  const startAudioMonitoring = useCallback((stream) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    analyserRef.current = analyser;

    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    
    const updateLevel = () => {
      if (!isRecording) return;
      analyser.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      setAudioLevel(average / 255);
      animationRef.current = requestAnimationFrame(updateLevel);
    };
    
    updateLevel();
  }, [isRecording]);

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      streamRef.current = stream;
      audioChunksRef.current = [];
      
      // Use webm format which is well supported
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Create audio blob
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        // Transcribe
        await transcribeAudio(audioBlob);
      };
      
      // Start recording
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      // Start audio level monitoring
      startAudioMonitoring(stream);
      
      toast.info('Enregistrement démarré. Parlez clairement.');
      
    } catch (error) {
      console.error('Error starting recording:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Accès au microphone refusé. Veuillez autoriser l\'accès dans les paramètres de votre navigateur.');
      } else {
        toast.error('Impossible de démarrer l\'enregistrement');
      }
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // Stop animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      
      setAudioLevel(0);
    }
  };

  // Transcribe audio
  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      if (appointmentId) formData.append('appointment_id', appointmentId);
      if (clientId) formData.append('client_id', clientId);
      
      const response = await api.post('/appointment-summaries/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000 // 60 second timeout for transcription
      });
      
      if (response.data?.text) {
        // Append to existing text
        const newText = text ? `${text}\n\n${response.data.text}` : response.data.text;
        setText(newText);
        
        if (onTranscriptionComplete) {
          onTranscriptionComplete(response.data.text, newText);
        }
        
        toast.success('Transcription terminée !');
      }
      
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la transcription');
    } finally {
      setIsTranscribing(false);
    }
  };

  // Save summary
  const handleSave = async () => {
    if (!text.trim()) {
      toast.error('Le compte-rendu est vide');
      return;
    }
    
    setIsSaving(true);
    
    try {
      if (onSave) {
        await onSave(text);
      }
      toast.success('Compte-rendu enregistré !');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setIsSaving(false);
    }
  };

  // Clear text
  const handleClear = () => {
    setText('');
    if (onTranscriptionComplete) {
      onTranscriptionComplete('', '');
    }
  };

  return (
    <div className={cn("space-y-3", className)}>
      {/* Recording Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        {!isRecording ? (
          <Button
            type="button"
            variant="outline"
            onClick={startRecording}
            disabled={isTranscribing}
            className="gap-2"
            data-testid="start-recording-btn"
          >
            <Mic className="w-4 h-4" />
            Dicter
          </Button>
        ) : (
          <Button
            type="button"
            variant="destructive"
            onClick={stopRecording}
            className="gap-2 animate-pulse"
            data-testid="stop-recording-btn"
          >
            <Square className="w-4 h-4" />
            Arrêter
          </Button>
        )}
        
        {/* Recording indicator */}
        {isRecording && (
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full bg-red-500 animate-pulse"
              style={{
                transform: `scale(${1 + audioLevel * 0.5})`,
                transition: 'transform 0.1s'
              }}
            />
            <span className="text-sm font-mono text-red-600">
              {formatTime(recordingTime)}
            </span>
            {/* Audio level bar */}
            <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-red-500 transition-all duration-100"
                style={{ width: `${audioLevel * 100}%` }}
              />
            </div>
          </div>
        )}
        
        {/* Transcribing indicator */}
        {isTranscribing && (
          <Badge variant="secondary" className="gap-1">
            <Loader2 className="w-3 h-3 animate-spin" />
            Transcription en cours...
          </Badge>
        )}
      </div>

      {/* Text Area */}
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        rows={6}
        className="resize-none"
        disabled={isRecording || isTranscribing}
        data-testid="dictation-textarea"
      />

      {/* Action buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {text && (
            <>
              <FileAudio className="w-3 h-3" />
              <span>{text.length} caractères</span>
            </>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {text && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              data-testid="clear-dictation-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Effacer
            </Button>
          )}
          
          {showSaveButton && text && (
            <Button
              type="button"
              onClick={handleSave}
              disabled={isSaving || isRecording || isTranscribing}
              size="sm"
              data-testid="save-dictation-btn"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1" />
              )}
              Enregistrer
            </Button>
          )}
        </div>
      </div>

      {/* Help text */}
      <p className="text-xs text-slate-500">
        💡 Cliquez sur &quot;Dicter&quot; pour enregistrer vocalement. La transcription sera ajoutée au texte existant.
      </p>
    </div>
  );
};

export default AudioDictation;
